import React, { useEffect, useMemo, useRef, useState } from 'react';
import './guessing.css';
import AudioPlayer from '../AudioPlayer';
import lobbyTheme from '../../sound/guessing/guesstitle.m4a';
import gameshowTheme from '../../sound/guessing/gameshow.mp3';
import guessAgainTheme from '../../sound/guessing/guessagain.mp3';
import lastGuessTheme from '../../sound/guessing/lastguess.mp3';

const defaultSettings = {
  mode: 'gameshow',
  hostSelectionEnabled: false,
  hintsEnabled: false,
  stealingEnabled: false,
  answerChoices: 4,
  spinnerMin: 0,
  spinnerMax: 3,
  categorySelectionMode: 'all',
  selectedCategoryIds: [],
};

const HostScreen = ({
  socket,
  ipAddress,
  sessionId,
  players,
  removePlayer,
  setForceRemove,
  onReturnToPartyPack,
  forcedMode,
  serverType,
  isMusicMuted = false,
}) => {
  const [settings, setSettings] = useState({ ...defaultSettings, mode: forcedMode || 'gameshow' });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [startError, setStartError] = useState('');
  const [state, setState] = useState({
    phase: 'lobby',
    mode: forcedMode || 'gameshow',
    board: [],
    scoreboard: [],
    currentQuestion: null,
    currentSelectorId: null,
    chosenHostId: null,
    spiral: null,
  });
  const [finalReview, setFinalReview] = useState([]);
  const clueAudioRef = useRef(null);
  const audioRevisionRef = useRef(0);

  const mediaControl = (action) => {
    socket.emit('guessingHostMediaControl', {
      sessionId,
      action,
    });
  };

  useEffect(() => {
    setSettings((prev) => ({ ...prev, mode: forcedMode || prev.mode }));
  }, [forcedMode]);

  useEffect(() => {
    if (!socket) return undefined;

    const updateState = (incoming) => setState((prev) => ({ ...prev, ...incoming }));

    socket.on('guessingState', updateState);
    socket.on('guessingFinalHostReview', ({ current }) => setFinalReview(current ? [current] : []));
    socket.on('guessingCategories', ({ categories }) => setAvailableCategories(categories || []));
    socket.on('guessingStartRejected', ({ reason }) => setStartError(reason || 'Unable to start game.'));

    socket.emit('guessingSyncState', { sessionId });
    socket.emit('guessingGetCategories', { sessionId });

    return () => {
      socket.off('guessingState', updateState);
      socket.off('guessingFinalHostReview');
      socket.off('guessingCategories');
      socket.off('guessingStartRejected');
    };
  }, [socket, sessionId]);

  useEffect(() => {
    const audio = clueAudioRef.current;
    if (!audio) return;

    const media = state.currentQuestion?.media;
    const source = state.currentQuestion?.audio || '';
    const revision = Number(media?.audioRevision || 0);
    const audioState = media?.audioState || 'stopped';

    if (!source) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audioRevisionRef.current = 0;
      return;
    }

    if (audio.getAttribute('src') !== source) {
      audio.setAttribute('src', source);
      audio.load();
    }

    if (revision !== audioRevisionRef.current) {
      audio.currentTime = 0;
      audioRevisionRef.current = revision;
    }

    if (audioState === 'playing') {
      audio.play().catch(() => {});
      return;
    }

    if (audioState === 'paused') {
      audio.pause();
      return;
    }

    audio.pause();
    audio.currentTime = 0;
  }, [
    state.currentQuestion?.audio,
    state.currentQuestion?.media?.audioState,
    state.currentQuestion?.media?.audioRevision,
    state.currentQuestion?.id,
  ]);

  const playersById = useMemo(() => {
    const map = new Map();
    players.forEach((p) => map.set(p.socketId, p));
    return map;
  }, [players]);

  const categoriesByPack = useMemo(() => {
    const grouped = new Map();
    availableCategories.forEach((category) => {
      const key = category.packId || category.packName || 'other';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(category);
    });

    return Array.from(grouped.entries()).map(([key, categories]) => ({
      key,
      packName: categories[0]?.packName || key,
      categories,
    }));
  }, [availableCategories]);

  const hostModeEnabled = Boolean(settings.hostSelectionEnabled || state.settings?.hostSelectionEnabled);
  const isProductionServer = serverType === 'fayaz.fly.dev';
  const activeMode = forcedMode || settings.mode || 'gameshow';
  const isSpiralMode = activeMode === 'spiral';
  const isGameShowMode = activeMode === 'gameshow';
  const inGuessingRound = state.phase !== 'lobby' && state.phase !== 'final-guess' && state.phase !== 'ended';
  const hasActiveQuestionAudio = Boolean(state.currentQuestion?.audio);
  const isLobbyMusic = isGameShowMode && state.phase === 'lobby';
  const isRoundOneMusic = isGameShowMode && Number(state.round || 1) === 1 && inGuessingRound && !hasActiveQuestionAudio;
  const isGuessAgainMusic = isGameShowMode && Number(state.round || 1) === 2 && inGuessingRound && !hasActiveQuestionAudio;
  const isLastGuessMusic = isGameShowMode && state.phase === 'final-guess';
  const activeFinalReview = state.finalGuess?.reviewCurrent;
  const filteredScoreboard = hostModeEnabled && state.chosenHostId
    ? state.scoreboard.filter((entry) => entry.id !== state.chosenHostId)
    : state.scoreboard;

  const toggleCategory = (categoryId) => {
    setSettings((prev) => {
      const selected = prev.selectedCategoryIds.includes(categoryId)
        ? prev.selectedCategoryIds.filter((id) => id !== categoryId)
        : [...prev.selectedCategoryIds, categoryId];
      return {
        ...prev,
        categorySelectionMode: 'custom',
        selectedCategoryIds: selected,
      };
    });
  };

  const startGame = () => {
    setStartError('');
    socket.emit('startGameGuessing', {
      sessionId,
      settings: {
        ...settings,
        mode: activeMode,
      },
    });
  };

  const voteLabel = (id) => playersById.get(id)?.name || (id ? 'Host' : 'Unassigned');
  const customCategoryReady = settings.categorySelectionMode === 'all' || settings.selectedCategoryIds.length > 0;
  const requiredPlayers = settings.hostSelectionEnabled ? 3 : 2;
  const selectedCategoryIdsForCount = settings.categorySelectionMode === 'all'
    ? availableCategories.map((category) => category.id)
    : settings.selectedCategoryIds;
  const selectedCategoryCount = selectedCategoryIdsForCount.length;
  const guessAgainEligible = selectedCategoryCount >= 6;

  return (
    <div className="guessing-tv-root">
      <AudioPlayer audioSrc={lobbyTheme} isPlaying={isLobbyMusic} isMuted={isMusicMuted} />
      <AudioPlayer audioSrc={gameshowTheme} isPlaying={isRoundOneMusic} isMuted={isMusicMuted} />
      <AudioPlayer audioSrc={guessAgainTheme} isPlaying={isGuessAgainMusic} isMuted={isMusicMuted} />
      <AudioPlayer audioSrc={lastGuessTheme} isPlaying={isLastGuessMusic} isMuted={isMusicMuted} />
      <audio ref={clueAudioRef} preload="auto" />
      {state.phase === 'lobby' && (
        <div className="guessing-tv-lobby">
          <div className="guessing-tv-lobby-hero">
            <h4>
              {isProductionServer
                ? `Join at PLAY.FAYAZ.ONE | Production Server | Session ${sessionId}`
                : `Join at PLAY.FAYAZ.ONE | Server ${ipAddress} | Session ${sessionId}`}
            </h4>
            <h1>{isSpiralMode ? 'Guessing Spiral' : 'Guessing Game Show'}</h1>
          </div>

          <div className="guessing-tv-lobby-grid">
            <section className="guessing-tv-card">
              <h2>Players ({players.length})</h2>
              <div className="guessing-tv-player-list">
                {players.map((player) => (
                  <button
                    className="ghost-button"
                    key={player.socketId}
                    onClick={() => {
                      setForceRemove(true);
                      removePlayer(player.socketId, true);
                    }}
                  >
                    {player.name}
                  </button>
                ))}
              </div>
              <p
                className="guessing-tv-fineprint"
                style={{
                  color: players.length < requiredPlayers ? "red" : "white",
                }}
              >
                Minimum players: {requiredPlayers}
              </p>
            </section>

            <section className="guessing-tv-card">
              <h2>Show Settings</h2>
              <label><input type="checkbox" checked={settings.hostSelectionEnabled} onChange={(e) => setSettings((prev) => ({ ...prev, hostSelectionEnabled: e.target.checked }))} /> Player host (voted)</label>
              <label><input type="checkbox" checked={settings.hintsEnabled} onChange={(e) => setSettings((prev) => ({ ...prev, hintsEnabled: e.target.checked }))} /> Hints</label>
              <label><input type="checkbox" checked={settings.stealingEnabled} onChange={(e) => setSettings((prev) => ({ ...prev, stealingEnabled: e.target.checked }))} /> Stealing</label>

              <label>
                Answer choices (no host)
                <input
                  type="number"
                  min={2}
                  max={6}
                  value={settings.answerChoices}
                  onChange={(e) => setSettings((prev) => ({ ...prev, answerChoices: Number(e.target.value) || 4 }))}
                />
              </label>

              <h4>Category selection</h4>
              <div className="segmented">
                <button
                  className={settings.categorySelectionMode === 'all' ? 'active' : ''}
                  onClick={() => setSettings((prev) => ({ ...prev, categorySelectionMode: 'all' }))}
                  type="button"
                >
                  All
                </button>
                <button
                  className={settings.categorySelectionMode === 'custom' ? 'active' : ''}
                  onClick={() => setSettings((prev) => ({ ...prev, categorySelectionMode: 'custom' }))}
                  type="button"
                >
                  Custom
                </button>
              </div>

              {settings.categorySelectionMode === 'custom' && (
                <div className="packs-scroll-box">
                  {categoriesByPack.map((packGroup) => (
                    <div key={packGroup.key}>
                      <h4>{packGroup.packName}</h4>
                      {packGroup.categories.map((category) => (
                        <label key={category.id} className="pack-row">
                          <input
                            type="checkbox"
                            checked={settings.selectedCategoryIds.includes(category.id)}
                            onChange={() => toggleCategory(category.id)}
                          />
                          {category.name} ({category.cardCount})
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {settings.mode === 'gameshow' && (
                <p
                  className="guessing-tv-fineprint"
                  style={{
                    color: guessAgainEligible ? '#78f2a0' : '#ffb9b9',
                    marginTop: 10,
                  }}
                >
                  {guessAgainEligible
                    ? `Guess Again enabled: ${selectedCategoryCount} categories selected (need at least 6).`
                    : `Guess Again skipped: ${selectedCategoryCount} categories selected (need at least 6).`}
                </p>
              )}

              {startError && <p className="guessing-tv-error">{startError}</p>}
            </section>
          </div>
            <button className="ghost-button" disabled={players.length < requiredPlayers || !customCategoryReady} onClick={startGame}>
              {isSpiralMode ? 'Start Spiral' : 'Start Guessing'}
            </button>
        </div>
      )}

      {state.phase !== 'lobby' && (
        <div className="guessing-tv-stage">
          <header className="guessing-tv-statusbar">
            <div className="guessing-tv-chip">Room: {ipAddress}</div>
            <div className="guessing-tv-chip">Phase: {state.phase}</div>
            <div className="guessing-tv-chip">Selector: {voteLabel(state.currentSelectorId)}</div>
            <div className="guessing-tv-chip">Host: {voteLabel(state.chosenHostId)}</div>
          </header>

          <main className="guessing-tv-main">
            {state.phase === 'host-vote' && (
              <section className="guessing-tv-clue-card">
                <h2>Vote for Host</h2>
                <p>Players vote on their phones. Chosen host controls question flow and judging from phone.</p>
              </section>
            )}

            {state.mode === 'gameshow' && state.phase === 'board' && (
              <section className="guessing-tv-board">
                {state.board?.map((category) => (
                  <article key={category.id} className="guessing-tv-category">
                    <h3>{category.name}</h3>
                    {category.cells.map((cell) => (
                      <div key={cell.id} className={`guessing-tv-cell ${cell.used ? 'used' : ''}`}>
                        {cell.used ? '' : `$${cell.value}`}
                      </div>
                    ))}
                  </article>
                ))}
              </section>
            )}

            {state.currentQuestion && (
              <section className="guessing-tv-jeopardy-card">
                <div className="guessing-tv-card-top">
                  <span>{state.currentQuestion.categoryName}</span>
                  <span>${state.currentQuestion.value}</span>
                </div>
                <h2>
                  {state.currentQuestion.status === 'locked'
                    ? `Answer: ${state.currentQuestion.answer || 'N/A'}`
                    : state.currentQuestion.question}
                </h2>
                {state.currentQuestion.media?.imageVisible && state.currentQuestion.image && (
                  <img src={state.currentQuestion.image} alt="Clue visual" className="guessing-tv-clue-image" />
                )}

                {!state.settings?.hostSelectionEnabled && state.chosenHostId && (
                  <div className="guessing-tv-media-controls">
                    {state.currentQuestion.image && (
                      <>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('show-image')}>Show Image</button>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('hide-image')}>Hide Image</button>
                      </>
                    )}
                    {state.currentQuestion.audio && (
                      <>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('play-audio')}>Play Audio</button>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('pause-audio')}>Pause Audio</button>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('stop-audio')}>Stop Audio</button>
                        <button className="ghost-button" type="button" onClick={() => mediaControl('restart-audio')}>Restart Audio</button>
                      </>
                    )}
                  </div>
                )}
                <div className="guessing-tv-hints">
                  {state.currentQuestion.status !== 'locked' && state.currentQuestion.hintsExposed?.map((hint, index) => (
                    <p key={hint}>Hint {index + 1}: {hint}</p>
                  ))}
                </div>
                {!state.settings?.hostSelectionEnabled && !state.chosenHostId && state.currentQuestion.choices?.length > 0 && (
                  <div className="guessing-tv-choice-list">
                    {state.currentQuestion.choices.map((choice) => (
                      <span key={choice} className="guessing-tv-choice-chip">{choice}</span>
                    ))}
                  </div>
                )}
              </section>
            )}

            {state.phase === 'final-guess' && (
              <section className="guessing-tv-clue-card">
                <h2>Last Guess</h2>
                <p>{state.finalGuess?.prompt?.question}</p>
                {activeFinalReview ? (
                  <div className="guessing-tv-final-review">
                    <p>
                      Reviewing: <strong>{activeFinalReview.name}</strong>
                    </p>
                    <p>Wager: ${activeFinalReview.wager}</p>
                    <p>Answer: {activeFinalReview.answer}</p>
                    {activeFinalReview.scored ? <p>Answer locked in.</p> : <p>Waiting for host judgment.</p>}
                  </div>
                ) : (
                  <p>All players submit wagers and answers from phones.</p>
                )}
                {!!finalReview.length && <p>Answers locked in on phone.</p>}
              </section>
            )}

            {state.phase === 'ended' && (
              <section className="guessing-tv-clue-card">
                <h2>Game Over</h2>
                <p>Final leaderboard is on player phones.</p>
              </section>
            )}
          </main>

          <footer className="guessing-tv-score-footer">
            {filteredScoreboard?.map((entry) => (
              <div
                className={`guessing-tv-player-tile ${state.currentQuestion?.buzzerId === entry.id ? 'active-buzzer' : ''}`}
                key={entry.id}
              >
                <span className="guessing-tv-player-name">{entry.name}</span>
                <strong className="guessing-tv-player-points">${entry.points}</strong>
              </div>
            ))}
          </footer>
        </div>
      )}
    </div>
  );
};

export default HostScreen;
